import { ArtBanners, BrandChips, CollectionBanners, CollectionCutouts, CollectionShowcase, Mosaic, PromoCards, WorldTiles } from "@/components/marketplace"
import type { CollectionLinksSection } from "@/features/feed/contract"
import { resolveFeedLink, resolveSeeAllLink } from "@/features/feed/resolve-link"
import { feedHeaderSize, SectionFrame } from "@/features/feed/sections/SectionFrame"
import type { FeedSectionProps } from "@/features/feed/sections/types"

export const CollectionLinksFeedSection = ({ section, context }: FeedSectionProps<CollectionLinksSection>) => {
  if (section.data.items.length === 0) return null
  const items = section.data.items.map((item) => ({
    id: item.id,
    eyebrow: item.eyebrow,
    title: item.title,
    accent: item.accent,
    sub: item.sub,
    cta: item.cta,
    image: item.image.url,
    imageAlt: item.image.alt,
    href: resolveFeedLink(item.link, context.countryCode, context.page),
    serveId: item.serveId,
  }))
  if (section.presentation.variant === "showcase") {
    return (
      <SectionFrame section={section} context={context} gut>
        <CollectionShowcase slides={items} />
      </SectionFrame>
    )
  }
  if (section.presentation.variant === "worlds" || section.presentation.variant === "mosaic") {
    const worlds = items.map((item) => ({ id: item.id, title: item.title, category: item.eyebrow, image: item.image, href: item.href, serveId: item.serveId }))
    return (
      <SectionFrame section={section} context={context}>
        {section.presentation.variant === "worlds" ? <WorldTiles items={worlds} /> : <Mosaic items={worlds} />}
      </SectionFrame>
    )
  }
  if (section.presentation.variant === "brands") {
    return (
      <SectionFrame section={section} context={context}>
        <BrandChips items={items.map((item) => ({ id: item.id, label: item.title, image: item.image, href: item.href, serveId: item.serveId }))} />
      </SectionFrame>
    )
  }
  if (section.presentation.variant === "promo") {
    return (
      <SectionFrame section={section} context={context}>
        <PromoCards
          items={items.map((item) => ({ id: item.id, eyebrow: item.eyebrow, title: item.title, image: item.image, href: item.href, serveId: item.serveId }))}
          priority={context.isLeadSection}
        />
      </SectionFrame>
    )
  }
  if (section.presentation.variant === "banners" && section.data.items.every((item) => !item.title && !item.eyebrow && !item.cta)) {
    return (
      <SectionFrame section={section} context={context}>
        <ArtBanners
          items={items.map((item) => ({ id: item.id, image: item.image, alt: item.imageAlt ?? "", href: item.href, serveId: item.serveId }))}
          priority={context.isLeadSection}
        />
      </SectionFrame>
    )
  }
  if (section.presentation.variant === "banners") {
    const { title } = section.presentation
    if (!title) return null
    const seeAllHref = resolveSeeAllLink(title.seeAll, section.type, context.countryCode, context.page)
    return (
      <SectionFrame section={section} context={context} ownsHeader>
        <CollectionBanners title={title.text} accent={title.accent} headerSize={feedHeaderSize(title.style)} numeral={title.numeral} seeAllHref={seeAllHref} items={items} />
      </SectionFrame>
    )
  }
  // `default` is the redesign. The four named variants below are the arrangements that
  // came before it and stay reachable for pages still authored against them.
  return (
    <SectionFrame section={section} context={context}>
      <CollectionCutouts items={items} priority={context.isLeadSection} />
    </SectionFrame>
  )
}
